// src/lib/roles.js
// STATUS: active | UPDATED: 2026-08-28
// LOG:
//   2026-08-28  New. Single source of truth for the tier system. Replaces two
//               diverging copies of ROLE_LABELS in Dashboard and Team, one of
//               which listed a `salesperson` role that the database constraint
//               does not allow, so it could never have matched anything.
// NEXT: dealership-level overrides, if a store ever calls a Sales Manager
//       something else. Phase 4 content governance, not before.
//
// The four levels, straight from Jazz's Build Plan page 1. `role` in the
// database is the enum the RLS policies check; the level and the label are how
// people talk about it.
//
//   00  admin      Jazz and Tyler        every store, creates stores
//   01  owner      Owner or GM           their whole store
//   02  manager    Sales Manager         their floor, the audit, sign-offs
//   03  employee   Salesperson / F&I     thirteen steps, one at a time
//
// Two naming decisions worth not relitigating:
//
// "Super Admin", not "Platform admin". Tyler's call, and he is right. Platform
// is software vocabulary. Nobody in a dealership says platform, and the label
// shows up in the header where Jazz will see it every day.
//
// Level 03 is NOT labelled "Member". Member is generic SaaS language and Jazz's
// style guide bans exactly that register. A salesperson calls himself a
// salesperson. But `employee` covers the whole floor, so where we know the
// person's job_function we say what they actually do: Ashley is a Business
// Manager in F&I, and calling her a Salesperson would be wrong in front of her
// own team.

export const LEVELS = {
  admin: '00',
  owner: '01',
  manager: '02',
  employee: '03',
}

// What a level 03 person is called, by department. Mirrors the job_function
// check constraint in migration 0001.
const JOB_FUNCTION_LABELS = {
  sales: 'Salesperson',
  fi: 'F&I',
  service: 'Service',
  parts: 'Parts',
  management: 'Management',
}

const ROLE_LABELS = {
  admin: 'Super Admin',
  owner: 'Owner',
  manager: 'Sales Manager',
  employee: 'Salesperson',
}

// The label to show for a person. Pass job_function when you have it so a
// level 03 in F&I does not get called a Salesperson.
export function roleLabel(role, jobFunction) {
  if (!role) return null
  if (role === 'employee' && jobFunction && JOB_FUNCTION_LABELS[jobFunction]) {
    return JOB_FUNCTION_LABELS[jobFunction]
  }
  return ROLE_LABELS[role] || role
}

export function levelFor(role) {
  return LEVELS[role] || null
}

// Who sees a teammate's position on the Roadmap. Mirrors the manager read
// policies in migration 0003. RLS is the enforcement, this only decides what
// the UI claims.
export const PROGRESS_ROLES = ['admin', 'owner', 'manager']

export function canSeeTeamProgress(role) {
  return PROGRESS_ROLES.includes(role)
}

// Level 00 has no dealership on purpose: a null dealership_id plus role admin
// is what current_user_is_admin() and the RLS policies key off for cross-store
// access. Anything that renders a store name has to handle this case.
export function isSuperAdmin(profile) {
  return profile?.role === 'admin' && !profile?.dealership_id
}

// Who may invite whom.
//
// THE SERVER IS AUTHORITATIVE. netlify/functions/invite-user.js checks this
// same matrix against the caller's own profile row, read with the service
// client, and nothing the browser sends can change the answer. This copy exists
// only so the UI does not offer a choice that is going to be refused, which is
// a worse experience than not offering it.
//
// Keep the two in step. If they ever drift, the server wins and the user sees a
// confusing 403 rather than a wrong outcome, which is the safe direction to
// drift in but still worth not doing.
export const CAN_GRANT = {
  admin: ['admin', 'owner', 'manager', 'employee'],
  owner: ['manager', 'employee'],
  manager: ['employee'],
  employee: [],
}

// A platform admin CAN now mint another platform admin, and it is the one
// action in this product that asks for a password again. See invite-user.js.
// Only an admin with no dealership may do it, and the server checks that against
// its own read of the caller's row, not against this list.
export function invitableRoles(role) {
  return CAN_GRANT[role] || []
}

export function canInvite(role) {
  return invitableRoles(role).length > 0
}
